import React from "react";
import { styled } from "@mui/material/styles";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { NODE_UI, stopPropagation } from "../../NodeUI";
import { buildDocEditorLocation } from "../docs/docCreationRunner";

const Card = styled("article")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: NODE_UI.spacing.md,
  padding: NODE_UI.spacing.lg,
  border: NODE_UI.border(theme),
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  textAlign: "left",
}));

const Header = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: NODE_UI.spacing.md,
});

const IconBox = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  flexShrink: 0,
  border: NODE_UI.border(theme),
  borderRadius: NODE_UI.borderRadiusSm,
  backgroundColor: theme.palette.background.subtle,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 1,
}));

const Actions = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 2,
  flexShrink: 0,
});

const IconAction = styled("a")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  padding: 0,
  border: "none",
  borderRadius: NODE_UI.borderRadiusSm,
  background: "transparent",
  color: theme.palette.text.disabled,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 1,
  },
}));

const RemoveButton = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  padding: 0,
  border: "none",
  borderRadius: NODE_UI.borderRadiusSm,
  background: "transparent",
  color: theme.palette.text.disabled,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.error.main,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 1,
  },
}));

const Body = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: NODE_UI.spacing.xs,
  minWidth: 0,
});

const Name = styled("h3")(({ theme }) => ({
  margin: 0,
  color: theme.palette.text.primary,
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  lineHeight: 1.25,
  wordBreak: "break-word",
}));

const Title = styled("p")(({ theme }) => ({
  margin: 0,
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.caption,
  lineHeight: 1.35,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

/**
 * Expanded card for an enabled skill — NodeUI styling.
 */
export default function SkillPickerCard({ skill, onRemove }) {
  const name = skill?.skillName || "";
  const title = skill?.title || "";
  const docId = skill?.docId || "";
  const label = name || title || "Untitled skill";
  const showTitle = Boolean(title && title !== name);
  const docHref = docId ? buildDocEditorLocation(docId) : "";

  return (
    <Card
      aria-label={`Enabled skill: ${label}`}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      <Header>
        <IconBox aria-hidden="true">
          {skill?.icon ? (
            <span>{skill.icon}</span>
          ) : (
            <BoltOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </IconBox>
        <Actions>
          {docHref ? (
            <IconAction
              href={docHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open skill doc: ${label}`}
              title="Open in Docs"
            >
              <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
            </IconAction>
          ) : null}
          {onRemove && name ? (
            <RemoveButton
              type="button"
              aria-label={`Remove skill: ${label}`}
              onClick={() => onRemove(name)}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </RemoveButton>
          ) : null}
        </Actions>
      </Header>

      <Body>
        <Name>{label}</Name>
        {showTitle ? <Title>{title}</Title> : null}
      </Body>
    </Card>
  );
}
